import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { Button } from "@nexus/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getDefaultRoute } from "@/features/auth/lib/page-routes";

interface PageErrorProps {
  is403?: boolean;
  onReset?: () => void;
  resetLabel?: string;
}

export function PageError({ is403, onReset, resetLabel }: PageErrorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const activeTenants = tenants.filter((t) => t.id === activeTenantId);
  const defaultRoute = getDefaultRoute(activeTenants);

  // Clear the persisted last route once it's known to be inaccessible — otherwise
  // the root `/` redirect keeps sending the user back to this same dead-end page.
  useEffect(() => {
    if (is403) useAuth.getState().setLastVisitedRoute("");
  }, [is403]);

  if (is403) {
    const handleNavigateAway = () => {
      void navigate({ to: (defaultRoute ?? "/login") as never });
    };

    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <div className="rounded-full bg-[var(--muted)] p-6">
          <ShieldAlert className="h-10 w-10 text-[var(--muted-foreground)]" />
        </div>
        <h1 className="text-xl font-normal">{t("errors.noAccess")}</h1>
        <p className="max-w-md text-[var(--muted-foreground)]">
          {t("errors.noAccessDescription")}
        </p>
        <Button variant="outline" onClick={handleNavigateAway}>
          {defaultRoute ? t("errors.goToAvailablePage") : t("errors.backToLogin")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
      <div className="rounded-full bg-[var(--muted)] p-6">
        <AlertTriangle className="h-10 w-10 text-[var(--muted-foreground)]" />
      </div>
      <h1 className="text-xl font-normal">{t("errors.pageLoadFailed")}</h1>
      <p className="max-w-md text-[var(--muted-foreground)]">
        {t("errors.pageLoadFailedDescription")}
      </p>
      {onReset && (
        <Button onClick={onReset}>
          {resetLabel ?? t("errors.goBack", "Go back")}
        </Button>
      )}
    </div>
  );
}
